import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { StreamableFile } from "@nestjs/common";
import {
    accountingReportQuery,
    accountingReportMultiComparisonQuery,
    accountingReportFinancialAccountExportQuery,
    accountingReportFinancialAccountQuery,
    accountingReportMultiComparisonExportQuery,
    getAccountTransactionsExportQuery,
    expenseByVendorRowVisibilityExportQuery,
    cashFlowStatementResponseWithPresentationSchema,
    getAccountTransactionsQuery,
    getAccountTransactionsResponseWithPresentationSchema,
    chartOfAccountsResponseSchema,
    trialBalanceResponseWithPresentationSchema,
    balanceSheetResponseWithPresentationSchema,
    profitLossResponseWithPresentationSchema,
    profitLossReportQuery,
    expenseByVendorResponseWithPresentationSchema,
    bankReconciliationReportResponseSchema,
} from "@common/schemas/domains/accounting/report.schema";
import {
    reportPackageExportQuery,
    reportPackageRowsExportBody,
} from "@common/schemas/domains/accounting/report-package.schema";
import {
    eliminationDetailReportResponseSchema,
    eliminationExceptionsReportResponseSchema,
} from "@common/schemas/domains/accounting/elimination.schema";
import {
    arAgingDetailExportQuery,
    arAgingDetailQuery,
    arAgingDetailResponseWithPresentationSchema,
    arAgingReportExportQuery,
    arAgingSummaryQuery,
    arAgingSummaryResponseWithPresentationSchema,
} from "@common/schemas/domains/accounting/ar-aging-report.schema";
import {
    checkDetailExportQuery,
    checkDetailReportQuery,
    checkDetailResponseWithPresentationSchema,
} from "@common/schemas/domains/accounting/check-detail-report.schema";
import { EliminationExceptionReason } from "@common/types/domains/accounting/elimination";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { AccountClass } from "../constants/domains/accounting/accounts";

const c = initContract();

export const accountingReportsContract = c.router(
    {
        getChartOfAccounts: {
            method: "GET",
            path: "/chart-of-accounts/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                availableClassifications: z
                    .array(z.nativeEnum(AccountClass))
                    .optional(),
                ledgerId: z.string().uuid().optional(),
            }),
            responses: {
                200: chartOfAccountsResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTrialBalance: {
            method: "GET",
            path: "/trial-balance",
            query: accountingReportFinancialAccountQuery,
            responses: {
                200: trialBalanceResponseWithPresentationSchema,
                404: ErrorMessageSchema,
            },
        },
        getBalanceSheet: {
            method: "GET",
            path: "/balance-sheet",
            query: accountingReportMultiComparisonQuery,
            responses: {
                200: balanceSheetResponseWithPresentationSchema,
                404: ErrorMessageSchema,
            },
        },
        getProfitAndLoss: {
            method: "GET",
            path: "/profit-loss",
            query: profitLossReportQuery,
            responses: {
                200: profitLossResponseWithPresentationSchema,
                404: ErrorMessageSchema,
            },
        },
        getAccountsTransactions: {
            method: "GET",
            path: "/account-transactions",
            query: getAccountTransactionsQuery,
            responses: {
                200: getAccountTransactionsResponseWithPresentationSchema,
                404: ErrorMessageSchema,
                400: ErrorMessageSchema,
            },
        },
        getEliminationDetailReport: {
            method: "GET",
            path: "/elimination-detail",
            query: z.object({
                workspaceId: z.string().uuid(),
                consolidationGroupId: z.string().uuid(),
                startDate: z.string(),
                endDate: z.string(),
                accountCode: z.coerce.number().int().optional(),
            }),
            responses: {
                200: eliminationDetailReportResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getEliminationExceptions: {
            method: "GET",
            path: "/elimination-exceptions",
            query: z.object({
                workspaceId: z.string().uuid(),
                consolidationGroupId: z.string().uuid(),
                startDate: z.string(),
                endDate: z.string(),
                // Numeric: matches EliminationPair.entityId (not a uuid).
                entityId: z.coerce.number().int().optional(),
                accountCode: z.coerce.number().int().optional(),
                reason: z.nativeEnum(EliminationExceptionReason).optional(),
            }),
            responses: {
                200: eliminationExceptionsReportResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportAccountsTransactions: {
            method: "GET",
            path: "/account-transactions/export",
            query: getAccountTransactionsExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                404: ErrorMessageSchema,
                400: ErrorMessageSchema,
            },
        },
        exportBalanceSheet: {
            method: "GET",
            path: "/balance-sheet/export",
            query: accountingReportMultiComparisonExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                404: ErrorMessageSchema,
            },
        },
        exportProfitAndLoss: {
            method: "GET",
            path: "/profit-loss/export",
            query: accountingReportMultiComparisonExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportTrialBalance: {
            method: "GET",
            path: "/trial-balance/export",
            query: accountingReportFinancialAccountExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportTaxPackage: {
            method: "GET",
            path: "/tax-package/export",
            query: accountingReportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                404: ErrorMessageSchema,
                400: ErrorMessageSchema,
            },
        },
        getExpenseByVendor: {
            method: "GET",
            path: "/expenses-by-vendor",
            query: accountingReportMultiComparisonQuery,
            responses: {
                200: expenseByVendorResponseWithPresentationSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportExpenseByVendor: {
            method: "GET",
            path: "/expenses-by-vendor/export",
            query: expenseByVendorRowVisibilityExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getCashFlowStatement: {
            method: "GET",
            path: "/cash-flow-statement",
            query: accountingReportFinancialAccountQuery,
            responses: {
                200: cashFlowStatementResponseWithPresentationSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportCashFlowStatement: {
            method: "GET",
            path: "/cash-flow-statement/export",
            query: accountingReportFinancialAccountExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportReportPackage: {
            method: "GET",
            path: "/report-package/export",
            query: reportPackageExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportReportPackageRows: {
            method: "POST",
            path: "/report-package/export",
            body: reportPackageRowsExportBody,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportBankReconciliation: {
            method: "GET",
            path: "/bank-reconciliation/:bankReconciliationId/export",
            pathParams: z.object({
                bankReconciliationId: z.string().uuid(),
            }),
            query: z.object({
                workspaceId: z.string().uuid(),
                generatedDate: z
                    .string()
                    .regex(/^\d{4}-\d{2}-\d{2}$/)
                    .optional(),
            }),
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getBankReconciliation: {
            method: "GET",
            path: "/bank-reconciliation/:bankReconciliationId",
            pathParams: z.object({
                bankReconciliationId: z.string().uuid(),
            }),
            query: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: bankReconciliationReportResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getArAgingSummary: {
            method: "POST",
            path: "/ar-aging-summary",
            body: arAgingSummaryQuery,
            responses: {
                200: arAgingSummaryResponseWithPresentationSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getArAgingDetail: {
            method: "POST",
            path: "/ar-aging-detail",
            body: arAgingDetailQuery,
            responses: {
                200: arAgingDetailResponseWithPresentationSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportArAgingSummary: {
            method: "POST",
            path: "/ar-aging-summary/export",
            body: arAgingReportExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportArAgingDetail: {
            method: "POST",
            path: "/ar-aging-detail/export",
            body: arAgingDetailExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getCheckDetail: {
            method: "POST",
            path: "/check-detail",
            body: checkDetailReportQuery,
            responses: {
                200: checkDetailResponseWithPresentationSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportCheckDetail: {
            method: "POST",
            path: "/check-detail/export",
            body: checkDetailExportQuery,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/accounting-reports",
    },
);
